import { useState, useCallback, useEffect } from 'react';
import { Search, Loader2, Database, AlertTriangle } from 'lucide-react';
import { libraryApi, getAuthToken } from '@/services/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

const BASE = process.env.REACT_APP_BACKEND_URL;

const scoreColor = (s) =>
  s >= 70 ? 'bg-teal-400 text-black' : s >= 50 ? 'bg-amber-400 text-black' : 'bg-zinc-700 text-zinc-300';

export default function LibrarySearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);

  const loadStatus = useCallback(async () => {
    try {
      setStatus(await libraryApi.indexStatus());
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const runSearch = useCallback(async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const data = await libraryApi.search(query.trim(), 24);
      setResults(data);
    } catch (err) {
      setError(err.message);
      setResults(null);
    } finally {
      setLoading(false);
    }
  }, [query]);

  const startIndex = useCallback(async () => {
    try {
      await libraryApi.startIndex();
      await loadStatus();
    } catch (e) {
      setError(e.message);
    }
  }, [loadStatus]);

  return (
    <div className="min-h-screen bg-[#0a0a0b] px-4 py-10 text-zinc-100 sm:px-6 lg:px-16" data-testid="library-search-page">
      <header className="max-w-6xl">
        <p className="text-xs uppercase tracking-[0.3em] text-teal-400/70">Reelia · índice semántico</p>
        <h1 className="mt-3 text-4xl font-semibold sm:text-5xl lg:text-6xl">Buscar en tu biblioteca</h1>
        <p className="mt-4 max-w-2xl text-sm text-zinc-400 md:text-base">
          Escribe lo que buscas en lenguaje natural. Cada escena de tus vídeos se ha descrito
          con IA y se compara por significado, no por nombre de fichero.
        </p>
      </header>

      {status && (
        <div className="mt-8 flex flex-col items-start gap-3 text-sm sm:flex-row sm:flex-wrap sm:items-center" data-testid="index-status">
          <span className="flex w-full items-start gap-2 rounded-2xl border border-zinc-800 bg-zinc-900/60 px-4 py-2 sm:w-auto">
            <Database className="mt-0.5 h-4 w-4 shrink-0 text-teal-400" />
            <span className="min-w-0 break-words text-xs sm:text-sm" data-testid="index-status-text">
              {status.videos_indexed}/{status.videos_total} vídeos · {status.scenes_searchable} escenas buscables
              {status.scenes_explicit > 0 && ` · ${status.scenes_explicit} excluidas`}
            </span>
          </span>
          {status.running ? (
            <span className="flex min-w-0 items-center gap-2 text-xs text-teal-300 sm:text-sm" data-testid="index-running">
              <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
              <span className="truncate">
                Indexando {status.progress.current_video} ({status.progress.videos_done}/{status.progress.videos_queued})
              </span>
            </span>
          ) : (
            <Button variant="outline" size="sm" onClick={startIndex} data-testid="start-index-btn">
              Indexar lo pendiente
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={loadStatus} data-testid="refresh-status-btn">
            Actualizar
          </Button>
        </div>
      )}

      <form onSubmit={runSearch} className="mt-10 flex max-w-2xl flex-col gap-3 sm:flex-row" data-testid="library-search-form">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="selfie frente al espejo, playa, gimnasio entrenando…"
            className="h-12 rounded-full border-zinc-800 bg-zinc-900/80 pl-11 text-base"
            data-testid="library-search-input"
          />
        </div>
        <Button type="submit" disabled={loading} className="h-12 w-full rounded-full px-7 sm:w-auto" data-testid="library-search-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Buscar'}
        </Button>
      </form>

      {error && (
        <div className="mt-6 flex items-center gap-2 rounded-lg border border-red-900/60 bg-red-950/40 px-4 py-3 text-sm text-red-300" data-testid="library-search-error">
          <AlertTriangle className="h-4 w-4" /> {error}
        </div>
      )}

      {results && (
        <section className="mt-10">
          <p className="text-sm text-zinc-500" data-testid="library-results-count">
            {results.count} resultados para «{results.query}»
          </p>
          {results.count === 0 && (
            <p className="mt-6 text-sm text-zinc-400" data-testid="library-no-results">
              No hay escenas que encajen. Prueba otra descripción o indexa más vídeos.
            </p>
          )}
          <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="library-results-grid">
            {results.results.map((s) => (
              <article key={s.id} className="group overflow-hidden rounded-xl border border-zinc-800 bg-zinc-900/50 transition-colors hover:border-teal-500/50" data-testid={`library-result-${s.id}`}>
                <div className="relative aspect-video overflow-hidden bg-black">
                  <img
                    src={`${BASE}${s.keyframe_url}?auth=${getAuthToken()}`}
                    alt={s.description}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className={`absolute left-3 top-3 rounded-full px-2.5 py-1 text-xs font-semibold ${scoreColor(s.score)}`} data-testid={`library-score-${s.id}`}>
                    {s.score}
                  </span>
                  <span className="absolute right-3 top-3 rounded-full bg-black/70 px-2.5 py-1 text-xs text-zinc-200">
                    {s.duration.toFixed(1)}s
                  </span>
                </div>
                <div className="p-4">
                  <p className="text-sm text-zinc-200" data-testid={`library-desc-${s.id}`}>{s.description}</p>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {s.tags.map((t) => (
                      <span key={t} className="rounded-full bg-zinc-800 px-2 py-0.5 text-xs text-zinc-400">{t}</span>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
